/**
 * Jovii — TRAILERS: who is allowed to play one, and when.
 *
 * A trailer is a whole second media pipeline. Measured (docs/METADATA.md § C2):
 * ONE YouTube iframe is 34 requests, 1.15 MB and 23 MB of JS heap; SIX are 202
 * requests, 6.98 MB and 103 MB. That number is the entire design:
 *
 *   • **One at a time, ever.** A single owner, claimed and released. A rail of
 *     twenty cards can never be a rail of twenty players. WHO gets the slot is
 *     decided by the app's single FOCUS (`focus.svelte.ts`): only the focused
 *     surface's dwell may claim, and losing the focus forfeits the claim —
 *     the banner tears its own trailer down the moment its attention goes
 *     (its `subject` key), a tile releases in its own effect teardown. So a
 *     focused tile always beats the banner, the banner cannot re-take the
 *     slot from a playing tile, and `claimTrailer`'s last-writer-wins is a
 *     safety net rather than an arbitration. (The hero's blurred ECHO iframe
 *     is scenery, not a trailer: it never claims, and is not counted here.)
 *   • **The still is the product.** `i.ytimg.com/vi/<id>/hqdefault.jpg` is 19.9 KB
 *     and available for 97 of 99 sampled ids. Everything shows the still; the
 *     iframe is the exception, not the rule, and a platform that refuses to
 *     autoplay (iOS in Low Power Mode) simply keeps the still — which is why the
 *     still has to look finished on its own.
 *   • **A film beats a trailer, always.** This app's measured bottleneck is the
 *     byte proxy at 7.45 MB/s, and two viewers already took 3.76 MB/s of it.
 *     Nothing here may compete with a film that is actually being watched — in
 *     this tab or in another one.
 *
 * ── THE FOUR REFUSALS ───────────────────────────────────────────────────────
 *
 * `mayAutoplay()` is a pure function so the rules can be argued with in a test
 * rather than in a browser. It says no when:
 *
 *   1. the visitor asked for less motion (`prefers-reduced-motion: reduce`);
 *   2. the visitor asked for less data (`navigator.connection.saveData`);
 *   3. a film is in progress somewhere in this browser;
 *   4. the tab is in the background, or there is no trailer to play.
 *
 * NONE OF THEM STOPS A DELIBERATE PRESS. Every one of these is about starting
 * something nobody asked for. The Trailer button on a title page always works:
 * somebody pressing "Trailer" has decided, and this file does not second-guess a
 * decision — it only declines to make one on their behalf.
 */

export {
  BRAND_MS,
  HERO_DWELL_MS,
  HERO_TRAILER_MAX_MS,
  REVEAL_FADE_MS,
  SILENCE_MS,
  STILL_HOLD_MS,
  TILE_CARD_STILL_MS,
  TILE_EXPAND_MS,
  TILE_PRELOAD_MS,
  TILE_TRAILER_MAX_MS,
  FILM_BEAT_MS,
  MEMO_TTL_MS,
  autoplayRefusal,
  beatFilmInProgress,
  endFilmInProgress,
  filmInProgress,
  mayAutoplay,
  resumeFrom,
  revealIn,
  revealVerdict,
  tileExpandDue,
  type AutoplayConditions,
  type AutoplayRefusal,
  type RevealInput,
  type RevealVerdict,
  type TrailerMemo
} from './trailer-rules';

import {
  filmInProgress,
  mayAutoplay,
  resumeFrom,
  type TrailerMemo
} from './trailer-rules';
import { stagePlayerState, tileStage } from './tile-stage.svelte';

function now(): number {
  return Date.now();
}

/* ────────────────────────────────────────────────────────────────────────────
 * The single owner
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The one place in the app that is allowed to have an iframe in it, and how far
 * into the trailer it has got.
 *
 * `startedAt` is what makes "clicking a playing trailer continues it rather than
 * restarting it" true. The embed is cross-origin and its `currentTime` is not
 * ours to read without the JS API, but the moment we created it IS ours, and a
 * trailer that has been on screen for eleven seconds is eleven seconds in. The
 * bigger view is then asked for `start=11`, so pressing a playing trailer picks
 * it up rather than winding it back.
 */
export const trailerState = $state<{ owner: string | null; ytId: string | null; startedAt: number }>({
  owner: null,
  ytId: null,
  startedAt: 0
});

/*
 * THE INSTRUMENT docs/TRAILERS.md § C4 asked for. A read-only window handle so
 * "it doesn't work on my phone" can become one screenshot: open the console and
 * read who holds the trailer slot, how far in it is, and what the refusal
 * conditions currently say. It writes nothing, it is not part of any code path,
 * and it costs one property on window.
 */
if (typeof window !== 'undefined') {
  Object.defineProperty(window, '__jtTrailer', {
    configurable: true,
    get: () => ({
      owner: trailerState.owner,
      ytId: trailerState.ytId,
      startedAt: trailerState.startedAt,
      elapsedSec: trailerElapsedSec(),
      autoSpent,
      heroPrimary: heroPresence.primary,
      conditions: { ...conditions },
      // The persistent tile player (tile-stage.svelte.ts): who it is showing
      // for, what, and whether the picture is ready — the iOS-death hunt's
      // one-screenshot instrument, extended to the stage. `playerState` is the
      // player's own answer (1 = actually rolling), which is how a hidden
      // preload that a device quietly suspended shows its hand.
      stage: { ...tileStage, playerState: stagePlayerState() }
    })
  });
}

/** Take the trailer slot. Whoever held it is dropped — there is only one. */
export function claimTrailer(owner: string, ytId: string): void {
  if (trailerState.owner === owner && trailerState.ytId === ytId) return;
  trailerState.owner = owner;
  trailerState.ytId = ytId;
  trailerState.startedAt = now();
}

/**
 * Where the last trailer had got to, so that pressing a playing tile and
 * landing on its title page picks the trailer up instead of winding it back.
 *
 * Short-lived on purpose: this is about the second between a press and the next
 * screen, not a viewing history.
 */
let memo: TrailerMemo | null = null;

/** Give it up. A no-op when somebody else already has it, so a late teardown
 *  cannot stop the trailer that replaced it. */
export function releaseTrailer(owner: string): void {
  if (trailerState.owner !== owner) return;
  if (trailerState.ytId !== null) {
    memo = { ytId: trailerState.ytId, sec: trailerElapsedSec(), at: now() };
  }
  trailerState.owner = null;
  trailerState.ytId = null;
  trailerState.startedAt = 0;
}

/**
 * How far into this trailer to start, given what was playing a moment ago.
 *
 * Counts the time spent getting here as well, because a trailer that was 11 s
 * in when the tile was pressed is 13 s in by the time the title page has drawn
 * — resuming at 11 would replay two seconds somebody has already watched.
 */
export function resumeSecFor(ytId: string): number {
  if (trailerState.owner !== null && trailerState.ytId === ytId) return trailerElapsedSec();
  return resumeFrom(memo, ytId, now());
}

export function holdsTrailer(owner: string): boolean {
  return trailerState.owner === owner;
}

/** Seconds into the trailer the current owner has reached, floored. */
export function trailerElapsedSec(): number {
  if (trailerState.owner === null) return 0;
  return Math.max(0, Math.floor((now() - trailerState.startedAt) / 1000));
}

/* ────────────────────────────────────────────────────────────────────────────
 * Reading the environment
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Live conditions, as reactive state, shared by every tile on the page — one
 * media-query listener and one visibility listener for the whole app rather
 * than one per card. `saveData` is read once because it does not change while a
 * page is open.
 */
export const conditions = $state({
  reducedMotion: false,
  saveData:
    typeof navigator !== 'undefined' &&
    (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true,
  backgrounded: false,
  film: false
});

let started = false;
let filmTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Start watching the environment. Idempotent, so every card may call it; the
 * listeners live for the life of the page, which is correct — they are three
 * booleans, and tearing them down when the last card unmounts would mean
 * rebuilding them when the next screen mounts a moment later.
 */
export function watchConditions(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  if (typeof window.matchMedia === 'function') {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = (): void => {
      conditions.reducedMotion = media.matches;
    };
    apply();
    if (typeof media.addEventListener === 'function') media.addEventListener('change', apply);
    else media.addListener(apply);
  }

  if (typeof document !== 'undefined') {
    const apply = (): void => {
      conditions.backgrounded = document.visibilityState === 'hidden';
    };
    apply();
    document.addEventListener('visibilitychange', apply);
  }

  const readFilm = (): void => {
    conditions.film = filmInProgress();
  };
  readFilm();
  // Polled rather than event-driven: the `storage` event does not fire in the
  // tab that wrote it and does not fire at all when the writing tab dies, and
  // "the film's tab was closed" is precisely the transition that has to be
  // noticed. Once every two seconds is free.
  const resumeFilm = (): void => {
    readFilm();
    if (filmTimer === null) filmTimer = setInterval(readFilm, 2000);
  };
  resumeFilm();
  window.addEventListener('pageshow', resumeFilm);
  window.addEventListener('pagehide', () => {
    if (filmTimer !== null) clearInterval(filmTimer);
    filmTimer = null;
  });
}

/**
 * HOW MANY TRAILERS THIS PAGE HAS STARTED BY ITSELF.
 *
 * A COUNT, NO LONGER A BUDGET. This used to feed a two-per-visit refusal, which
 * the cycling hero spent on its first two slides — the owner's QA (2026-08-16):
 * "the trailers never start again". The rule is retired in `trailer-rules.ts`
 * (the guards that remain are documented there); the count stays because the
 * callers already report it and it is a useful debug read.
 *
 * Not a rune. It is written the moment a trailer starts, inside the effects
 * that start them, so as reactive state it would re-run those effects and tear
 * down the trailer it had just built — the same trap the banner's
 * `bannerSpent` documents.
 */
let autoSpent = 0;

/** Called by whoever starts an automatic trailer, never by a deliberate press. */
export function spendAutoTrailer(): void {
  autoSpent += 1;
}

/** How many automatic trailers this visit has started. For tests and a debug read. */
export function autoTrailersSpent(): number {
  return autoSpent;
}

/**
 * THE BANNER'S PAGE-TOP PRIMACY, as one shared boolean.
 *
 * Written only by `Hero.svelte` (see `heroPrimary` in trailer-rules.ts for the
 * rule and the measurement), read by every tile through `autoplayAllowed`.
 * While it is true a tile's AUTOMATIC trailer is refused — the banner is what
 * the visitor is looking at — and a deliberate press was never subject to any
 * of this.
 */
export const heroPresence = $state({ primary: false });

export function setHeroPrimacy(on: boolean): void {
  heroPresence.primary = on;
}

/** The live answer for a given surface. Tiles are the default on purpose —
 *  Poster.svelte calls this with one argument and needs no new knowledge. */
export function autoplayAllowed(hasTrailer: boolean, surface: 'hero' | 'tile' = 'tile'): boolean {
  return mayAutoplay({
    hasTrailer,
    reducedMotion: conditions.reducedMotion,
    saveData: conditions.saveData,
    filmInProgress: conditions.film,
    backgrounded: conditions.backgrounded,
    surface,
    heroPrimary: heroPresence.primary
  });
}

/** Tests only. */
export function resetTrailersForTests(): void {
  memo = null;
  autoSpent = 0;
  trailerState.owner = null;
  trailerState.ytId = null;
  trailerState.startedAt = 0;
  heroPresence.primary = false;
  conditions.reducedMotion = false;
  conditions.saveData = false;
  conditions.backgrounded = false;
  conditions.film = false;
}
